import type { ITenantDestination } from '@/entities/tenant-destination'
import type { TenantDestinationFormValue } from '@/features/tenant-destination'
import { useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCreateTenantDestinationMutation,
  useDeleteTenantDestinationMutation,
  useTenantDestinationsQuery,
  useUpdateTenantDestinationMutation,
} from '@/entities/tenant-destination'
import {
  MasterPickerDialog,
  TenantDestinationFormDrawer,
  TenantDestinationListTable,
} from '@/features/tenant-destination'
import { AdminScopeType, DEFAULT_FETCH_LIMIT } from '@/shared/constants'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_FORM: TenantDestinationFormValue = {
  banner: '',
  thumbnail: '',
}

interface TenantDestinationManagerProps {
  tenantId: string
  autoOpenPicker?: boolean
  returnTo?: string
}

export function TenantDestinationManager({
  tenantId,
  autoOpenPicker,
  returnTo,
}: TenantDestinationManagerProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { scopeType } = useAdminScope()
  const [isPickerOpen, setIsPickerOpen] = useState(Boolean(autoOpenPicker))
  const [selectedMasterId, setSelectedMasterId] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [formValue, setFormValue] = useState(DEFAULT_FORM)
  const [editingItem, setEditingItem] = useState<ITenantDestination | null>(null)
  const [deletingItem, setDeletingItem] = useState<ITenantDestination | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [prevTenantId, setPrevTenantId] = useState(tenantId)

  if (tenantId !== prevTenantId) {
    setPrevTenantId(tenantId)
    setPage(1)
  }

  const tenantDestination = useTenantDestinationsQuery({
    current: page,
    pageSize,
    isActive: true,
    ...(scopeType === AdminScopeType.Super && tenantId ? { tenantId } : {}),
  })
  // * VI: Chỉ nạp danh sách đã liên kết khi picker mở, vì nó chỉ dùng để loại trùng trong dialog. | EN: Only load the linked list while the picker is open, since it is used solely for dedupe inside the dialog.
  const usedTenantDestinations = useTenantDestinationsQuery(
    {
      current: 1,
      pageSize: DEFAULT_FETCH_LIMIT,
      ...(scopeType === AdminScopeType.Super && tenantId ? { tenantId } : {}),
    },
    { enabled: isPickerOpen },
  )
  const createMutation = useCreateTenantDestinationMutation()
  const updateMutation = useUpdateTenantDestinationMutation()
  const deleteMutation = useDeleteTenantDestinationMutation()
  const saving = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending
  const scopedTenantId = scopeType === AdminScopeType.Super && tenantId ? tenantId : undefined
  const data = useMemo(
    () => tenantDestination.data?.data.data ?? [],
    [tenantDestination.data?.data.data],
  )
  const excludedDestinationIds = useMemo(() => {
    const ids = new Set<string>()

    for (const item of usedTenantDestinations.data?.data.data ?? []) {
      if (item.destinationId) {
        ids.add(item.destinationId)
      }
    }

    return ids
  }, [usedTenantDestinations.data?.data.data])

  async function handleCreateFromMaster() {
    if (!selectedMasterId) {
      showToast.error(
        t('tenantDestination.selectMasterRequired', {
          defaultValue: 'Please pick a destination first',
        }),
      )
      return
    }
    try {
      await createMutation.mutateAsync({
        ...(scopedTenantId ? { tenantId: scopedTenantId } : {}),
        payload: {
          destinationId: selectedMasterId,
          banner: formValue.banner.trim() || null,
          thumbnail: formValue.thumbnail.trim() || null,
        },
      })
      showToast.success(
        t('common.createSuccess', {
          defaultValue: 'Tenant destination created successfully',
        }),
      )
      setFormOpen(false)
      setFormValue(DEFAULT_FORM)
      setSelectedMasterId('')
      if (returnTo) {
        void navigate({ to: returnTo as never })
      }
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  async function handleEditSubmit() {
    if (!editingItem) {
      return
    }
    try {
      await updateMutation.mutateAsync({
        id: editingItem.id,
        ...(scopedTenantId ? { tenantId: scopedTenantId } : {}),
        payload: {
          banner: formValue.banner.trim() || null,
          thumbnail: formValue.thumbnail.trim() || null,
        },
      })
      setEditingItem(null)
      setFormOpen(false)
      setFormValue(DEFAULT_FORM)
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  async function handleToggleStatus(item: ITenantDestination, checked: boolean) {
    try {
      await updateMutation.mutateAsync({
        id: item.id,
        ...(scopedTenantId ? { tenantId: scopedTenantId } : {}),
        payload: { isActive: checked },
      })
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-zinc-200 bg-white p-4">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-zinc-900">
              {t('tenantDestination.title', { defaultValue: 'Tenant destinations' })}
            </h3>
            <p className="mt-1 text-xs text-zinc-500">
              {t('tenantDestination.subtitle', {
                defaultValue: 'Link shared destinations and manage tenant-specific media/status.',
              })}
            </p>
          </div>
          <Button onClick={() => setIsPickerOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('tenantDestination.addFromPool', { defaultValue: 'Create tenant destination' })}
          </Button>
        </div>

        <div className="rounded-lg border border-zinc-200">
          <TenantDestinationListTable
            data={data}
            loading={tenantDestination.isLoading}
            pagination={{
              current: page,
              pageSize,
              total: tenantDestination.data?.data.total ?? 0,
            }}
            onPageChange={(nextPage, nextPageSize) => {
              setPage(Number(nextPage))
              setPageSize(Number(nextPageSize))
            }}
            onEdit={(item) => {
              setEditingItem(item)
              setFormValue({
                banner: item.banner ?? '',
                thumbnail: item.thumbnail ?? '',
              })
              setFormOpen(true)
            }}
            onToggleStatus={(item, checked) => {
              void handleToggleStatus(item, checked)
            }}
            onDelete={(item) => setDeletingItem(item)}
          />
        </div>
      </div>

      <MasterPickerDialog
        open={isPickerOpen}
        excludedDestinationIds={excludedDestinationIds}
        exclusionLoading={usedTenantDestinations.isPending}
        exclusionError={usedTenantDestinations.isError}
        onOpenChange={setIsPickerOpen}
        onSelect={(master) => {
          setSelectedMasterId(master.id)
          setIsPickerOpen(false)
          setEditingItem(null)
          setFormValue(DEFAULT_FORM)
          setFormOpen(true)
        }}
      />

      <ConfirmDialog
        open={Boolean(deletingItem)}
        title={t('tenantDestination.deleteConfirm', { defaultValue: 'Delete tenant destination?' })}
        description={t('tenantDestination.deleteConfirmDesc', {
          defaultValue:
            'This action cannot be undone. The destination will be permanently removed.',
        })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={deleteMutation.isPending}
        onCancel={() => setDeletingItem(null)}
        onConfirm={async () => {
          if (!deletingItem || deleteMutation.isPending) {
            return
          }
          try {
            await deleteMutation.mutateAsync({
              id: deletingItem.id,
              ...(scopedTenantId ? { tenantId: scopedTenantId } : {}),
            })
            setDeletingItem(null)
          } catch (error) {
            showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
          }
        }}
      />

      <TenantDestinationFormDrawer
        open={formOpen}
        title={
          editingItem
            ? t('tenantDestination.edit', { defaultValue: 'Edit tenant destination' })
            : t('tenantDestination.create', { defaultValue: 'Create tenant destination' })
        }
        value={formValue}
        saving={saving}
        onChange={setFormValue}
        onOpenChange={setFormOpen}
        onSubmit={() => {
          if (editingItem) {
            void handleEditSubmit()
            return
          }
          void handleCreateFromMaster()
        }}
      />
    </div>
  )
}
